import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { invalidateProductLists } from './useProductMutation';

/**
 * 15-B-3 (ID90) — 상품 일괄 변경/엑셀 import BO 클라이언트.
 *
 * BE 계약(catalog `BackofficeCatalogController`):
 *  - `POST /admin/v1/products/bulk-update`  {productIds[], status?, displayed?, primaryCategoryId?, listPrice?, salePrice?} → ProductBulkResultView
 *  - `POST /admin/v1/products/import` (multipart file) → ProductBulkResultView
 *
 * generated types 에 아직 없는 엔드포인트라 adminFetch + 멱등키로 호출한다
 * (popups/notifications hook 과 동일 관용). 성공 시 상품 목록 캐시를 무효화한다.
 *
 * 참고(15-B-1 ID88 다중삭제): 일괄삭제는 Lead 결정(b)로 신규 bulk 엔드포인트가 아닌 단건
 * DELETE 순차 호출(orders 패턴, `useDeleteProductMutation` 루프) 사용 — 이 hook 에 포함하지 않는다.
 */

export type DisplayProductStatus = 'ON_SALE' | 'SALE_ENDED' | 'HIDDEN' | 'PREPARING';

export interface MoneyInput {
  amount: number;
  currencyCode: string;
}

/** 부분 시맨틱: undefined/누락 = 미변경. 최소 1필드는 호출부에서 보장. */
export interface BulkUpdateInput {
  productIds: ReadonlyArray<string>;
  status?: DisplayProductStatus;
  displayed?: boolean;
  primaryCategoryId?: string;
  listPrice?: MoneyInput;
  salePrice?: MoneyInput;
  /**
   * QA-589 1인 구매 수량 제한 — 있으면 고른 상품 모두 이 값으로(min/max null = 제한 없음). 없으면 변경 안 함.
   * BE bulk-update 반영 전(BO-TO-BE-2026-10-27-bulk-update-qa589)에는 받지 않는다.
   */
  purchaseLimit?: { basis: 'OPTION' | 'PRODUCT' | 'MEMBER'; min: number | null; max: number | null };
}

export interface BulkFailure {
  productId: string;
  reason: string;
}

export interface ProductBulkResult {
  requested: number;
  succeeded: number;
  skipped: number;
  failed: ReadonlyArray<BulkFailure>;
}

const BASE = '/admin/v1/products';

// 일괄변경(대표 카테고리 포함)·엑셀 import 도 카테고리 매핑을 바꾼다 — 상품 저장과 같은 무효화.
const invalidate = invalidateProductLists;

const parseResult = async (res: Response, label: string): Promise<ProductBulkResult> => {
  if (!res.ok) throw new Error(`${label} failed: ${res.status}`);
  const env = (await res.json()) as { data?: ProductBulkResult };
  if (!env.data) throw new Error(`${label}: no data`);
  return env.data;
};

export const useProductBulkMutation = () => {
  const qc = useQueryClient();

  // 15-B-3 (ID90) — 선택 상품 공통 속성 일괄 변경.
  const bulkUpdate = useMutation<ProductBulkResult, Error, BulkUpdateInput>({
    mutationFn: async (input) => {
      const body: Record<string, unknown> = { productIds: [...input.productIds] };
      if (input.status !== undefined) body.status = input.status;
      if (input.displayed !== undefined) body.displayed = input.displayed;
      if (input.primaryCategoryId !== undefined) body.primaryCategoryId = input.primaryCategoryId;
      if (input.listPrice !== undefined) body.listPrice = input.listPrice;
      if (input.salePrice !== undefined) body.salePrice = input.salePrice;
      // QA-589 — 구매 수량 제한도 본문에(빠뜨리면 이것만 바꿀 때 BE 가 "변경할 항목 없음" 400).
      if (input.purchaseLimit !== undefined) body.purchaseLimit = input.purchaseLimit;
      const res = await adminFetch(`${BASE}/bulk-update`, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', `${BASE}/bulk-update`, body) },
        body: JSON.stringify(body),
      });
      return parseResult(res, 'bulk-update');
    },
    onSuccess: () => invalidate(qc),
  });

  // 15-B-3 (ID90) — 엑셀 import(업로드, 9컬럼 일괄 변경). round-trip: export → 편집 → import.
  const importExcel = useMutation<ProductBulkResult, Error, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append('file', file);
      const res = await adminFetch(`${BASE}/import`, {
        method: 'POST',
        body: form,
      });
      return parseResult(res, 'import');
    },
    onSuccess: () => invalidate(qc),
  });

  // #17 — 전체 필드 엑셀 import(업로드). 상품코드 미존재=신규 등록(CREATE), 존재=전체 교체(UPDATE).
  // be-catalog ProductFullExcelService(/import-full)와 정합. 9컬럼 일괄변경(/import)과 별도.
  const importExcelFull = useMutation<ProductBulkResult, Error, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append('file', file);
      const res = await adminFetch(`${BASE}/import-full`, {
        method: 'POST',
        body: form,
      });
      return parseResult(res, 'import-full');
    },
    onSuccess: () => invalidate(qc),
  });

  return { bulkUpdate, importExcel, importExcelFull };
};
